/**
 * English catalog keyed by the Chinese source string.
 *
 * Missing keys fall back to the Chinese text, so pages can be migrated
 * incrementally: an untranslated string simply stays in Chinese.
 * Placeholders use {name} and are substituted after lookup.
 */
export const en: Record<string, string> = {
  // App shell
  '首页': 'Home',
  '主线空间': 'Mainline Space',
  '主线需求': 'Mainline Requirements',
  '暂无主线项目': 'No mainline projects',
  '主线设计文档': 'Mainline Design Docs',
  '项目空间': 'Project Space',
  '工具': 'Tools',
  '需求同步': 'Requirement Sync',
  '需求回流': 'Requirement Flowback',
  '系统配置': 'System Config',
  '用户管理': 'User Management',
  '插件配置': 'Plugin Config',

  // Project submenus
  '底软交付物': 'BSP Deliverables',
  '需求': 'Requirements',
  '设计材料': 'Design Materials',
  'Momenta释放产物包': 'Momenta Release Packages',
  '未分组': 'Ungrouped',
  '暂无可访问的项目': 'No accessible projects',
  '暂无可访问的项目，请联系管理员分配权限': 'No accessible projects. Please contact the administrator to request access.',
  '暂无可访问的项目，请联系管理员配置权限': 'No accessible projects. Please contact the administrator to configure access.',
  '编辑项目': 'Edit project',

  // Header / user menu
  'BCR管理系统': 'BCR Management System',
  '查看 API 文档': 'View API documentation',
  '提交反馈': 'Submit Feedback',
  '内部用户': 'Internal user',
  '外部用户': 'External user',
  '获取 API Key': 'Get API Key',
  '退出登录': 'Sign out',
  '加载中...': 'Loading...',

  // Common actions
  '确定': 'OK',
  '取消': 'Cancel',
  '保存': 'Save',
  '提交': 'Submit',
  '删除': 'Delete',
  '编辑': 'Edit',
  '新建': 'Create',
  '刷新': 'Refresh',
  '搜索': 'Search',
  '重置': 'Reset',
  '关闭': 'Close',
  '操作': 'Actions',
  '状态': 'Status',
  '备注': 'Remarks',
  '详情': 'Details',
  '返回列表': 'Back to list',
  '暂无数据': 'No data',
  '是': 'Yes',
  '否': 'No',

  // Login
  '底软包交付管理系统': 'BSP Package Delivery Management',
  '用户名': 'Username',
  '密码': 'Password',
  '登录': 'Sign in',
  '请输入用户名': 'Please enter your username',
  '请输入密码': 'Please enter your password',
  '登录成功': 'Signed in successfully',
  '内部用户请使用企业账号登录': 'Internal users: please sign in with your corporate account',

  // Not found
  '抱歉，您访问的页面不存在': 'Sorry, the page you visited does not exist.',
  '返回首页': 'Back to Home',

  // Feedback
  '反馈类型': 'Feedback type',
  '选择反馈类型': 'Select a feedback type',
  '新功能': 'New feature',
  '缺陷': 'Bug',
  '优化': 'Improvement',
  '其他': 'Other',
  '请选择反馈类型': 'Please select a feedback type',
  '主题': 'Subject',
  '请输入反馈主题': 'Please enter a subject',
  '主题长度不超过100字符': 'Subject must be at most 100 characters',
  '简洁的反馈主题': 'A concise subject',
  '详细描述': 'Details',
  '请输入反馈描述': 'Please enter your feedback',
  '描述长度不超过1000字符': 'Details must be at most 1000 characters',
  '详细描述您的反馈内容': 'Describe your feedback in detail',
  '感谢您的反馈！': 'Thank you for your feedback!',
  '反馈提交失败，请稍后重试': 'Failed to submit feedback. Please try again later.',

  // API key modal
  '生成 API Key': 'Generate API Key',
  'API Key 生成成功，请立即复制保存': 'API key generated. Copy and save it now.',
  'API Key 生成失败': 'Failed to generate API key',
  '已复制到剪贴板': 'Copied to clipboard',
  '用途说明': 'About this key',
  'API Key 用于在 Python/Bash 脚本中调用 BSP 外部接口，如文件下载接口。生成后请妥善保管，不要泄露给他人。':
    'API keys let Python/Bash scripts call BSP external APIs, such as file download endpoints. Keep the key safe and never share it.',
  'Key 名称（可选）': 'Key name (optional)',
  '为 Key 设置一个易识别的名称，留空则自动生成时间戳名称':
    'Give the key a recognizable name. Leave blank to auto-generate a timestamped name.',
  '例如：生产环境脚本使用': 'e.g. production scripts',
  '⚠️ 请立即复制保存': '⚠️ Copy and save this key now',
  '此 Key 仅显示一次，关闭后将无法再次查看完整密钥。请复制到安全的地方妥善保管。':
    'This key is shown only once. After closing, the full key cannot be viewed again. Store it in a safe place.',
  'Key 名称：': 'Key name:',
  'Python 使用示例': 'Python example',
  '复制 Key': 'Copy key',
  '已保存，关闭': 'Saved, close',

  // Requirement subcategories / option labels
  'MCU及中间件': 'MCU & Middleware',
  '通讯': 'Communication',
  '传感器': 'Sensor',
  'SoC及中间件': 'SoC & Middleware',
  '诊断和故障': 'Diagnostics & Faults',
  '时间同步': 'Time Sync',
  '升级和上下电': 'Upgrade & Power',
  '功能安全': 'Functional Safety',
  '网络安全': 'Cybersecurity',
  '必选': 'Required',
  '可选': 'Optional',
  '通用': 'General',
  '日志': 'Log',
  '存储': 'Storage',

  // Upload / file
  '{name} 上传成功': '{name} uploaded',
  '{name} 上传失败': '{name} upload failed',
  '{name} 上传失败: {error}': '{name} upload failed: {error}',
  '上传失败': 'Upload failed',
  '上传文件': 'Upload file',
  '上传更多文件': 'Upload more files',
  '重新上传': 'Retry upload',
  '重试': 'Retry',
  '选择文件重试': 'Choose file to retry',
  '点击或拖拽文件到此区域上传': 'Click or drag a file here to upload',
  '支持单个文件上传': 'Single file upload only',
  '合并中，请稍候...': 'Merging, please wait...',
  '提交后上传': 'Uploads on submit',
  '排队中': 'Queued',
  '上传中': 'Uploading',
  '正在上传 {name}... {percent}%': 'Uploading {name}... {percent}%',
  '已上传 {count}/{max} 个文件': '{count}/{max} files uploaded',
  '* 请至少上传一个文件': '* Please upload at least one file',
  '最多只能上传{max}个文件': 'You can upload at most {max} files',
  '最多只能选择{max}个文件，已自动截取前{max}个':
    'You can select at most {max} files; the first {max} were kept',
  '已选择 {count} 个文件': '{count} files selected',
  '请至少选择一个文件': 'Please select at least one file',
  '选择已有': 'Select existing',
  '选择已有文件': 'Select existing files',
  '确认选择': 'Confirm selection',
  '从项目已有文件中选择一个文件': 'Select one file from existing project files',
  '从项目已有文件中选择（可多选）': 'Select from existing project files (multiple allowed)',
  '请选择文件': 'Please select a file',
  '请选择文件（可多选）': 'Please select files (multiple allowed)',
  '暂无同类型文件': 'No files of this type',
  '(选已有)': '(existing)',
  '(待删除)': '(to be deleted)',
  '完整MD5: {md5}': 'Full MD5: {md5}',
  '备注（选填）': 'Remarks (optional)',
  '删除文件': 'Delete file',
  '取消删除标记': 'Cancel deletion mark',
  '标记删除（更新后生效）': 'Mark for deletion (applies on save)',
  '删除文件失败': 'Failed to delete file',
  '文件已删除': 'File deleted',
  '确认删除': 'Confirm deletion',
  '确定要删除文件 {name} 吗？': 'Are you sure you want to delete {name}?',
  '确认': 'Confirm',
  '自动选择': 'Auto',
  '直连上传': 'Direct upload',
  '代理上传': 'Proxy upload',

  // Search bar
  '搜索包名称/版本/车型': 'Search by package name, version, or vehicle model',
  '选择项目': 'Select project',
  '文件类型': 'File type',
  '开始日期': 'Start date',
  '结束日期': 'End date',

  // Conflict dialog
  '数据冲突': 'Data conflict',
  '该记录已被其他用户修改': 'This record was modified by another user',
  '您正在编辑的数据已被其他用户更新。请选择如何处理此冲突。':
    'The data you are editing was updated by another user. Choose how to resolve this conflict.',
  '您的版本号': 'Your version',
  '服务器版本号': 'Server version',
  '最后修改人': 'Last modified by',
  '最后修改时间': 'Last modified at',
  '冲突字段:': 'Conflicting fields:',
  '您的值:': 'Your value:',
  '服务器值:': 'Server value:',
  '放弃修改': 'Discard changes',
  '刷新查看最新': 'Refresh to see latest',
  '强制覆盖': 'Force overwrite',

  // Rich text editor
  '图片上传失败': 'Image upload failed',
  '图片上传失败: {status} {statusText}': 'Image upload failed: {status} {statusText}',
  '图片上传失败: 未收到OBS确认': 'Image upload failed: no confirmation from OBS',
  '按住alt键比例缩放': 'Hold Alt to resize proportionally',
  '输入大小后按回车确认': 'Enter a size and press Enter to confirm',
  '输入内容... 支持粘贴图片和超链接': 'Enter content... images and hyperlinks supported',

  // Dashboard
  '项目总览': 'Project Overview',
  '交付物上传列表': 'Deliverable Upload List',
  '交付物上传看板': 'Deliverable Upload Dashboard',
  'BCR看板': 'BCR Dashboard',
  '需求回流看板': 'Requirement Flowback Dashboard',
  '可访问项目': 'Accessible projects',
  '用户类型': 'User type',
  '角色': 'Role',
  '管理员': 'Administrator',
  '普通用户': 'Standard user',
  '欢迎, {name}': 'Welcome, {name}',
  '{count} 个项目': '{count} projects',

  // Upload board / stats
  '项目名': 'Project',
  '交付物ID': 'Deliverable ID',
  '交付类型': 'Deliverable type',
  '来源': 'Source',
  '上传类型': 'Upload type',
  '交付物名称': 'Deliverable name',
  '车型': 'Vehicle model',
  '文件数量': 'Files',
  '文件大小': 'Size',
  '上传状态': 'Upload status',
  '上传者': 'Uploaded by',
  '开始上传时间': 'Upload start',
  '结束上传时间': 'Upload end',
  '上传时长': 'Duration',
  '平均网速': 'Avg. speed',
  '关联文件': 'Linked files',
  '说明': 'Description',
  '完成': 'Done',
  '更新': 'Update',
  '重传': 'Re-upload',
  '只显示': 'Show only',
  '不显示': 'Hide',
  '选择项目名': 'Select a project',
  '确认删除此上传记录？': 'Delete this upload record?',
  '共 {total} 条': 'Total {total} items',
  '{count} 个文件': '{count} files',
  '{count} 个': '{count}',
  '未变更文件 ({count})': 'Unchanged files ({count})',
  '新增文件 ({count})': 'Added files ({count})',
  '上传失败文件 ({count})': 'Failed files ({count})',
  '删除文件 ({count})': 'Deleted files ({count})',
  '自动重试: {count}次': 'Auto retries: {count}',
  '手动重试: {count}次': 'Manual retries: {count}',
  '上传时间: {start} ~ {end}': 'Upload time: {start} ~ {end}',
  '上传次数': 'Uploads',
  '上传成功': 'Successful uploads',
  '上传中: {count}': 'Uploading: {count}',
  '成功率': 'Success rate',
  '失败率': 'Failure rate',
  '总次数': 'Total',
  '成功': 'Success',
  '失败': 'Failed',
  '项目': 'Project',
  '项目 {id}': 'Project {id}',
  '刷新数据': 'Refresh data',
  '{period}汇总': '{period} summary',
  '所选范围内暂无上传数据': 'No upload data in the selected range',
  '上传趋势': 'Upload trend',
  '来源分布': 'Source distribution',
  '按项目分布（Top 15）': 'By project (Top 15)',
  '来源明细': 'Source details',
  '项目明细': 'Project details',
  '近7天': 'Last 7 days',
  '近30天': 'Last 30 days',
  '上周': 'Last week',
  '上个月': 'Last month',
  '今日': 'Today',
  '本周': 'This week',
  '本月': 'This month',
  '自定义': 'Custom',
  '全部项目': 'All projects',
  '全部来源': 'All sources',

  // BCR dashboard
  '按周': 'Weekly',
  '按月': 'Monthly',
  '暂无BCR任务数据': 'No BCR task data',
  '任务数': 'Tasks',
  '任务结果': 'Task result',
  '失败次数': 'Failure count',
  'BCR任务总数': 'Total BCR tasks',
  '失败任务': 'Failed tasks',
  '平均耗时': 'Avg. duration',
  '分钟': 'min',
  '每周BCR任务趋势': 'Weekly BCR task trend',
  '每月BCR任务趋势': 'Monthly BCR task trend',
  '成功率分布': 'Success rate distribution',
  '各阶段失败分布': 'Failures by stage',
  '暂无阶段失败数据': 'No stage failure data',
  'Top 10 失败原因': 'Top 10 failure reasons',
  '暂无失败原因数据': 'No failure reason data',

  // Flowback dashboard
  '(未知)': '(Unknown)',
  '已合入': 'Merged',
  '暂无回流数据': 'No flowback data',

  // Package management
  'BSP 底软包管理': 'BSP Package Management',
  '{name} - BSP 底软包': '{name} - BSP Package',
  '新建底软包': 'New BSP package',
  '编辑底软包': 'Edit BSP package',
  '查看详情': 'View details',
  '制品包': 'Artifact package',
  '操作日志': 'Operation logs',
  '操作日志 - {name}': 'Operation logs - {name}',
  '文件上传进度': 'File upload progress',
  '上传时间': 'Upload time',
  '等待': 'Waiting',
  '确定要删除 "{name}" 吗？此操作不可恢复。': 'Are you sure you want to delete "{name}"? This cannot be undone.',
  '删除成功': 'Deleted',
  '刷新成功': 'Refreshed',
  '刷新失败': 'Refresh failed',
  '操作失败': 'Operation failed',
  '更新成功': 'Updated',
  '更新失败': 'Update failed',
  '请选择项目': 'Please select a project',
  '获取包详情失败': 'Failed to load package details',
  '获取操作日志失败': 'Failed to load operation logs',
  '该记录已被其他用户修改。是否强制覆盖？': 'This record was modified by another user. Overwrite anyway?',
  '{name} - 详情': '{name} - Details',
  '加载底软包详情失败': 'Failed to load package details',
  '底软包不存在': 'Package not found',
  '返回': 'Back',
  '上传中...': 'Uploading...',
  '下载链接已复制到剪贴板': 'Download link copied to clipboard',
  '复制下载链接失败': 'Failed to copy download link',
  '复制下载链接': 'Copy download link',
  '下载': 'Download',
  '获取下载链接失败': 'Failed to get download link',
  '未查询到产物包下载链接': 'No artifact download link found',
  '基本信息': 'Basic information',
  '包名称': 'Package name',
  '版本号': 'Version',
  '子车型': 'Sub-models',
  '硬件平台': 'Hardware platform',
  '创建时间': 'Created at',
  '更新时间': 'Updated at',
  '上传模式': 'Upload mode',
  '分片大小': 'Chunk size',
  '固定文件': 'Firmware files',
  '文档文件': 'Document files',
  '底软供应商上传文件': 'Supplier uploads',
  '关联需求': 'Linked requirements',
  '关联OPL问题': 'Linked OPL issues',
  '暂无固定文件': 'No firmware files',
  '暂无文档文件': 'No document files',
  '暂无关联需求': 'No linked requirements',
  '暂无关联OPL问题': 'No linked OPL issues',
  'Git仓库:': 'Git repository:',
  '分支:': 'Branch:',
  '产物包名称:': 'Artifact name:',
  '备注:': 'Remarks:',
  '(Git源)': '(Git source)',
  'Git源': 'Git source',
  '内容:': 'Content:',
  '附件:': 'Attachments:',
  '测试报告': 'Test report',
  'Release Note 不能为空，请保留文件或输入内容': 'Release Note is required. Keep a file or enter content.',
  '测试报告不能为空，请保留文件或输入内容': 'Test report is required. Keep a file or enter content.',
  '请输入底软包名称': 'Please enter the package name',
  '输入底软包名称': 'Enter package name',
  '选择车型': 'Select vehicle models',
  '选择子车型（可选）': 'Select sub-models (optional)',
  '请先选择车型': 'Select vehicle models first',
  '选择硬件平台（可选）': 'Select hardware platforms (optional)',
  '关联Momenta释放产物包': 'Linked Momenta releases',
  '请选择关联的Momenta释放产物包（可多选，选填）': 'Select linked Momenta releases (multiple, optional)',
  '输入底软包说明信息': 'Enter package description',
  '上传': 'Upload',
  '输入': 'Input',
  'Git仓库URL (必填)': 'Git repository URL (required)',
  'Git分支 (必填)': 'Git branch (required)',
  'Git Commit ID (与Tag二选一)': 'Git Commit ID (or Tag)',
  'Git Tag (与Commit二选一)': 'Git Tag (or Commit)',
  'URL和分支已从上一版本自动填写': 'URL and branch were prefilled from the previous version',
  '清空Git配置': 'Clear Git settings',
  'Artifact产物包名称 (必填)': 'Artifact package name (required)',
  '删除该 Artifact': 'Delete this artifact',
  '添加 Artifact': 'Add artifact',
  '修改文件类型': 'Change file type',
  '文件名：': 'File name:',
  '当前类型：': 'Current type:',
  '新类型：': 'New type:',
  '选择新的文件类型': 'Select a new file type',
  '获取已有文件列表失败': 'Failed to load existing files',
  '无法更新新上传的文件类型，请删除后重新上传': 'Cannot change the type of a newly uploaded file. Delete and upload it again.',
  '无效的文件类型': 'Invalid file type',
  '文件类型已更新': 'File type updated',
  '更新文件类型失败': 'Failed to update file type',
  '请至少上传一个固件文件或提供Git配置': 'Upload at least one firmware file or provide Git settings',
  '请上传 Release Note 文件': 'Please upload a Release Note file',
  '请输入 Release Note 内容': 'Please enter Release Note content',
  '输入 Release Note 内容': 'Enter Release Note content',
  '请上传测试报告文件': 'Please upload a test report file',
  '请输入测试报告内容': 'Please enter test report content',
  '输入测试报告内容': 'Enter test report content',
  '{code} 的Git信息填写不完整。请填写完整的Git URL、分支，以及Commit ID或Tag（至少选填其一）。':
    'Incomplete Git information for {code}. Provide the Git URL, branch, and either a Commit ID or Tag.',
  '{name} 中存在同名文件：{names}，请删除重复文件后再提交':
    '{name} contains duplicate file names: {names}. Remove the duplicates before submitting.',
  '{name} 中存在重复的 Artifact 产物包名：{names}，请修改后再提交':
    '{name} contains duplicate artifact names: {names}. Fix them before submitting.',
  '测试报告中存在同名文件：{names}，请删除重复文件后再提交':
    'The test report contains duplicate file names: {names}. Remove the duplicates before submitting.',
  'Git信息不完整 (需要Commit ID或Tag): {code}': 'Incomplete Git information (a Commit ID or Tag is required): {code}',
  'Artifact名称不能为空: {code}': 'Artifact name cannot be empty: {code}',
  '文件不存在: {code}': 'File does not exist: {code}',
  '更新成功，文件正在后台上传中...': 'Updated. Files are uploading in the background...',
  '创建成功，文件正在后台上传中...': 'Created. Files are uploading in the background...',
  '以下文件上传失败，请编辑重试：{files}': 'These files failed to upload. Edit and retry: {files}',
  '数据已被其他用户修改，请刷新后重试': 'This record was modified by another user. Refresh and try again.',
  '删除失败': 'Delete failed',
  'ℹ️ 已有文本内容将被保留': 'ℹ️ Existing text content will be kept',
  'ℹ️ 已有附件将被保留': 'ℹ️ Existing attachments will be kept',
  '复制外部访问链接': 'Copy external access link',
  '复制外部访问链接：{url}': 'Copy external access link: {url}',
  '复制失败，请手动复制': 'Copy failed. Please copy manually.',
  '外部访问链接已复制': 'External access link copied',
  '已复制': 'Copied',

  // JLR SharePoint upload
  '上传到 JLR SharePoint': 'Upload to JLR SharePoint',
  '该平台需要 Microsoft 账号认证': 'This platform requires Microsoft account authentication',
  '提交账号密码后，系统会生成一个校验数字；请在手机的 Microsoft Authenticator 里输入该数字并批准，然后回来点击「确认上传」。密码仅在本次请求中转发，不会保存在 BSP。':
    'After submitting your credentials, a verification number is generated. Enter it in Microsoft Authenticator on your phone and approve, then come back and click "Confirm upload". Your password is forwarded only for this request and is not stored in BSP.',
  'Microsoft 账号密码': 'Microsoft account password',
  '请填写账号': 'Please enter your account',
  '请填写密码': 'Please enter your password',
  '父目录链接': 'Parent folder link',
  '请填写父目录链接': 'Please enter the parent folder link',
  '父目录链接不能超过 500 个字符': 'The parent folder link must be at most 500 characters',
  '文件夹': 'Folder',
  '请填写目标文件夹': 'Please enter the target folder',
  '文件夹不能超过 255 个字符': 'The folder must be at most 255 characters',
  '父目录下的文件夹名，支持多级（如 drop12/sw1/pike）。不存在会自动新建；目录里已有同名文件时整批失败，不覆盖。':
    'Folder path under the parent folder, multi-level allowed (e.g. drop12/sw1/pike). Missing folders are created automatically; if a file with the same name exists, the whole batch fails without overwriting.',
  '默认已填好 package_release 目录，一般不需要改。': 'The package_release folder is filled in by default; usually no change is needed.',
  '例如 wenqiang.zhao.momenta@jlrglobal.onmicrosoft.com': 'e.g. wenqiang.zhao.momenta@jlrglobal.onmicrosoft.com',
  '例如 drop12/sw1/pike_20261029': 'e.g. drop12/sw1/pike_20261029',
  '上传目录：': 'Upload folder:',
  '提交并获取校验数字': 'Submit and get verification number',
  '校验数字': 'Verification number',
  '请在 Microsoft Authenticator 中批准登录': 'Approve the sign-in in Microsoft Authenticator',
  '打开手机上的 Microsoft Authenticator，在弹窗里输入下面这个数字，然后点「批准」。批准完成后再点下方的「确认上传」。':
    'Open Microsoft Authenticator on your phone, enter the number below in the prompt, then tap Approve. After approving, click "Confirm upload".',
  '认证已直接完成，可点击下方「确认上传」。': 'Authentication completed. Click "Confirm upload" below.',
  '返回修改': 'Back to edit',
  '确认上传': 'Confirm upload',
  '确认上传失败，请重试': 'Upload confirmation failed. Please try again.',
  '启动认证失败，请检查账号密码后重试': 'Failed to start authentication. Check your credentials and try again.',
  '提交账号密码后，系统会生成一个校验数字；请在手机的 Microsoft Authenticator 里输入该数字并批准，登录成功后即可选择上传路径。密码仅在本次请求中转发，不会保存在 BSP。':
    'After submitting your credentials, a verification number is generated. Enter it in Microsoft Authenticator on your phone and approve; once signed in you can choose upload paths. Your password is forwarded only for this request and is not stored in BSP.',
  '认证已失效，请重新输入账号密码': 'Authentication expired. Please enter your credentials again.',
  '打开手机上的 Microsoft Authenticator，在弹窗里输入下面这个数字，然后点「批准」。批准后这里会自动进入下一步。':
    'Open Microsoft Authenticator on your phone, enter the number below in the prompt, then tap Approve. Once approved, the next step opens automatically.',
  '认证已直接完成，正在确认登录状态…': 'Signed in directly. Confirming the session…',
  '尚未收到批准': 'Approval not received yet',
  '请在手机上完成批准后，点击下方「我已批准，继续」。':
    'Approve on your phone, then click "I approved, continue" below.',
  '我已批准，继续': 'I approved, continue',

  // JLR SharePoint multi-path upload plan
  '选择上传路径': 'Choose upload paths',
  '重新认证': 'Sign in again',
  '开始上传': 'Start upload',
  '获取上传文件列表失败，请重试': 'Failed to load the upload file list. Please try again.',
  '上传路径': 'Upload paths',
  '新建路径': 'New path',
  '所有文件将上传到父链接根目录': 'All files will be uploaded to the parent folder root',
  '如需分类存放，点击「新建路径」在父链接下新建子目录。':
    'To organize files, click "New path" to create a subfolder under the parent folder.',
  '默认路径': 'Default path',
  '路径 {index}': 'Path {index}',
  '个文件': 'files',
  '所有未指定其它路径的文件都放在这里。':
    'All files not assigned to another path go here.',
  '勾选即把文件移入该路径；一个文件只能属于一个路径，取消勾选会回到默认路径。':
    'Checking a file moves it into this path. A file belongs to exactly one path; unchecking returns it to the default path.',
  '该产物包没有可上传的原始文件': 'This package has no raw files to upload',
  '飞书文档': 'Feishu documents',
  '转换为 PDF': 'Convert to PDF',
  '转 PDF': 'PDF',
  '水印内容（可留空）': 'Watermark text (optional)',
  '水印会平铺在每一页上，最多 100 个字符。':
    'The watermark is tiled on every page, up to 100 characters.',
  '不支持转 PDF': 'Cannot convert to PDF',
  '以下 {count} 个飞书文档不会上传': '{count} Feishu documents will not be uploaded',
  '请填写所有上传路径，或删除空路径':
    'Enter a path for every row, or delete the empty ones',
  '上传路径不能重复': 'Upload paths must be unique',
  '没有可上传的文件': 'No files to upload',
  '一次最多上传 {count} 个文件': 'At most {count} files can be uploaded at once',
  '登录已失效': 'Sign-in expired',
  '请点击下方「重新认证」后再提交': 'Click "Sign in again" below, then submit',
  '提交上传失败': 'Failed to submit the upload',
  '根目录': 'Root folder',
  '跳过 {count} 个已存在的文件': '{count} existing files skipped',
  '正在转换飞书文档为 PDF': 'Converting Feishu documents to PDF',
  '{count} 个飞书文档未转换，本次不会上传': '{count} Feishu documents were not converted and will not be uploaded',

  // Batch 6: requirements module
  '(空)': '(empty)',
  'BVP 服务暂时不可用，请稍后重试': 'BVP service is temporarily unavailable, please try again later',
  'TAG1保存失败': 'Failed to save TAG1',
  'TAG2保存失败': 'Failed to save TAG2',
  'TAG保存失败': 'Failed to save TAG',
  'reject (已拒绝)': 'reject (Rejected)',
  'released (供应商已接收)': 'released (Accepted by supplier)',
  '{count} 个车型共用此列表': '{count} vehicle models share this list',
  '{count} 个附件上传失败': '{count} attachments failed to upload',
  '{count} 个附件上传成功': '{count} attachments uploaded',
  '{count} 条': '{count} items',
  '{count} 条需求删除失败': 'Failed to delete {count} requirements',
  '{count}/20 · 只选一个': '{count}/20 · Select only one',
  '{count}/20 字符 · 只能选择一个阶段': '{count}/20 characters · Only one stage can be selected',
  '{n} 个月前': '{n} months ago',
  '{n} 分钟前': '{n} minutes ago',
  '{n} 天前': '{n} days ago',
  '{n} 小时前': '{n} hours ago',
  '{n} 年前': '{n} years ago',
  '{reqId} 不在当前列表': '{reqId} is not in the current list',
  '一级分类': 'Primary category',
  '上传附件': 'Upload attachment',
  '不能超过20个字符（当前：{count}）': 'Cannot exceed 20 characters (current: {count})',
  '主线': 'Mainline',
  '主线同步提示': 'Mainline sync notice',
  '主线项目 {projects} 包含被删除需求，已自动删除': 'Mainline project(s) {projects} contained the deleted requirement; it was removed automatically',
  '主线项目 {projects} 包含该需求，已自动删除': 'Mainline project(s) {projects} contained this requirement; it was removed automatically',
  '主线项目 {projects} 包含该需求，已自动完成更新': 'Mainline project(s) {projects} also contain this requirement; they were updated automatically',
  '二级分类': 'Secondary category',
  '交付物': 'Deliverable',
  '交付阶段': 'Delivery stage',
  '交付阶段不能为空': 'Delivery stage cannot be empty',
  '交付阶段长度不能超过20个字符（当前：{count}）': 'Delivery stage cannot exceed 20 characters (current: {count})',
  '从飞书回流需求': 'Sync requirements from Feishu',
  '优先级': 'Priority',
  '例如：V1.0': 'e.g. V1.0',
  '保存冲突: 该需求已被其他用户修改，请刷新后重试': 'Save conflict: this requirement was modified by another user. Please refresh and try again',
  '保存失败，请重试': 'Failed to save, please try again',
  '修改:': 'Modified:',
  '修改TAG': 'Edit TAG',
  '修改TAG1': 'Edit TAG1',
  '修改TAG2': 'Edit TAG2',
  '修改状态': 'Change status',
  '修改需求来源': 'Change requirement source',
  '全选': 'Select all',
  '共 {total} 个版本': '{total} versions in total',
  '共 {total} 条变更记录': '{total} change records in total',
  '内容': 'Content',
  '刚刚': 'Just now',
  '创建': 'Created',
  '创建失败，请稍后重试': 'Creation failed, please try again later',
  '创建操作': 'Create',
  '创建版本标签': 'Create version tag',
  '删除:': 'Deleted:',
  '删除后，该版本的变更记录将被清除，下次发布时会重新生成。': 'After deletion, the change records of this version will be cleared and regenerated on the next release.',
  '删除失败，请重试': 'Failed to delete, please try again',
  '删除操作': 'Delete',
  '删除版本': 'Delete version',
  '删除评论失败': 'Failed to delete comment',
  '删除附件失败': 'Failed to delete attachment',
  '加载变更记录失败': 'Failed to load change records',
  '加载变更预览失败': 'Failed to load change preview',
  '加载失败': 'Failed to load',
  '加载版本列表失败': 'Failed to load versions',
  '加载评论失败': 'Failed to load comments',
  '加载附件失败': 'Failed to load attachments',
  '加载需求列表失败': 'Failed to load requirements',
  '单独保存需求来源': 'Save requirement source separately',
  '发布于': 'Released on',
  '发布人': 'Released by',
  '发布失败: {reason}': 'Release failed: {reason}',
  '发送': 'Send',
  '发送变更通知': 'Send change notification',
  '发送失败，请重试': 'Failed to send, please try again',
  '发送评论失败': 'Failed to send comment',
  '变更前:': 'Before:',
  '变更后:': 'After:',
  '变更统计': 'Change summary',
  '变更通知已发送（{count} 条需求，合并为 1 封邮件）': 'Change notification sent ({count} requirements, merged into 1 email)',
  '同步到主线项目': 'Sync to mainline projects',
  '图片上传中...': 'Uploading image...',
  '图片上传中，请稍候...': 'Uploading image, please wait...',
  '基础项目': 'Base project',
  '字段验证失败': 'Field validation failed',
  '定位到该需求': 'Locate requirement',
  '客户原始需求': 'Customer requirement',
  '客户原始需求 ({ids})': 'Customer requirement ({ids})',
  '对比基准版本': 'Baseline version',
  '导出中...': 'Exporting...',
  '导出失败，请重试': 'Export failed, please try again',
  '导出成功！': 'Export successful!',
  '将删除需求:': 'Requirements to delete:',
  '将删除需求：': 'Requirements to delete:',
  '将该需求加入通知列表？': 'Add this requirement to the notification list?',
  '展开': 'Expand',
  '展开详情': 'Expand details',
  '山头「{name}」下': 'Under mountain "{name}"',
  '已删除 {count} 条需求': 'Deleted {count} requirements',
  '已同步到主线项目：{projects}': 'Synced to mainline projects: {projects}',
  '已选 {count} 项': '{count} selected',
  '底软包名称': 'BSP package name',
  '底软需求': 'BSP requirements',
  '当前无未发布的变更': 'No unpublished changes',
  '总变更数:': 'Total changes:',
  '总览': 'Overview',
  '您没有权限在该项目中创建需求': 'You do not have permission to create requirements in this project',
  '批量删除': 'Bulk delete',
  '批量更新需求状态': 'Bulk update requirement status',
  '描述': 'Description',
  '搜索内容': 'Search content',
  '搜索需求编号或描述': 'Search requirement ID or description',
  '操作人': 'Operator',
  '操作日志 — {id}': 'Operation logs — {id}',
  '收起详情': 'Collapse details',
  '收起详细变更记录': 'Collapse detailed change records',
  '数据加载失败': 'Failed to load data',
  '新增:': 'Added:',
  '新增需求': 'New requirement',
  '无匹配需求': 'No matching requirements',
  '无字段变更': 'No field changes',
  '无描述': 'No description',
  '无法加载项目阶段，已显示标准阶段': 'Failed to load project stages; standard stages are shown',
  '无法解析：无权访问所属车型或需求已删除': 'Cannot resolve: no access to the vehicle model, or the requirement was deleted',
  '无详细变更信息': 'No detailed change information',
  '无需求编辑权限': 'No permission to edit requirements',
  '时间': 'Time',
  '暂无变更记录': 'No change records',
  '暂无可选值': 'No values available',
  '暂无总览数据': 'No overview data',
  '暂无操作日志': 'No operation logs',
  '暂无版本': 'No versions',
  '暂无版本标签，无法进行变更对比': 'No version tag available; cannot compare changes',
  '暂无评论': 'No comments',
  '暂无附件': 'No attachments',
  '暂无需求数据': 'No requirement data',
  '暂无需求数据可导出': 'No requirement data to export',
  '更新评论失败': 'Failed to update comment',
  '有 {count} 条需求在当前山头内无法解析（无权访问所属车型或需求已删除），请先移除后再发送': '{count} requirements cannot be resolved under this mountain (no access to the vehicle model, or deleted). Remove them before sending',
  '服务器内部错误，请查看服务器日志': 'Internal server error. Please check the server logs',
  '未指定': 'Unspecified',
  '未知用户': 'Unknown user',
  '未设置': 'Not set',
  '查看文档': 'View document',
  '查看文档 {index}': 'View document {index}',
  '查看详细变更记录（含变更前后值）': 'View detailed change records (with before/after values)',
  '标准阶段': 'Standard stages',
  '此操作将同时删除该需求的所有评论和附件，且不可恢复。': 'This will also delete all comments and attachments of this requirement. This cannot be undone.',
  '此操作将同时删除这些需求的所有评论和附件，且不可恢复。': 'This will also delete all comments and attachments of these requirements. This cannot be undone.',
  '没有需要保存的变更': 'No changes to save',
  '添加过滤条件': 'Add filter',
  '点击展开查看详情': 'Click to expand details',
  '点击收起': 'Click to collapse',
  '版本': 'Version',
  '版本 {version} 已删除': 'Version {version} deleted',
  '版本信息': 'Version information',
  '版本信息不能超过5000字': 'Version information cannot exceed 5000 characters',
  '版本加载失败': 'Failed to load versions',
  '版本变更查看': 'Version changes',
  '版本号已存在': 'Version number already exists',
  '版本号格式必须为 Vx.x（例如：V1.0）': 'Version number must match Vx.x (e.g. V1.0)',
  '版本标签创建成功': 'Version tag created',
  '状态更新': 'Status update',
  '状态更新失败': 'Failed to update status',
  '硬件': 'Hardware',
  '确认删除版本 {version}？': 'Delete version {version}?',
  '确认删除选中的 {count} 条需求？': 'Delete the {count} selected requirements?',
  '确认删除需求？': 'Delete this requirement?',
  '确认删除？': 'Delete?',
  '移除': 'Remove',
  '自定义TAG1': 'Custom TAG1',
  '自定义TAG2': 'Custom TAG2',
  '自定义TAG3': 'Custom TAG3',
  '设计文档': 'Design document',
  '评论': 'Comment',
  '评论已删除': 'Comment deleted',
  '评论已更新': 'Comment updated',
  '该操作创建了新的需求记录': 'This operation created a new requirement record',
  '该操作删除了需求记录': 'This operation deleted the requirement record',
  '该需求可能被过滤条件或分类页签隐藏，详情仍可正常查看。': 'This requirement may be hidden by filters or the category tab; its details are still accessible.',
  '该项目复用「{name}」的需求，请前往': 'This project reuses the requirements of "{name}". Go to',
  '请从左侧选择一个需求查看详情': 'Select a requirement on the left to view details',
  '请描述本次版本的主要内容和变更...': 'Describe the main content and changes of this version...',
  '请详细描述需求...': 'Describe the requirement in detail...',
  '请输入二级分类（可选）': 'Enter secondary category (optional)',
  '请输入版本信息': 'Enter version information',
  '请输入版本号': 'Enter version number',
  '请输入设计文档链接（例如：https://example.com/doc.pdf）': 'Enter design document URL (e.g. https://example.com/doc.pdf)',
  '请输入设计文档链接（可选）': 'Enter design document URL (optional)',
  '请输入需求备注信息（可选）': 'Enter requirement notes (optional)',
  '请输入需求描述': 'Enter requirement description',
  '请输入验证准则': 'Enter verification criteria',
  '请输入验证准则（可选）': 'Enter verification criteria (optional)',
  '请选择一级分类': 'Select primary category',
  '请选择交付阶段': 'Select delivery stage',
  '请选择优先级': 'Select priority',
  '请选择硬件': 'Select hardware',
  '请选择验证方法': 'Select verification method',
  '责任人': 'Assignee',
  '输入名字搜索': 'Search by name',
  '输入客户原始需求ID，回车确认': 'Enter customer requirement ID and press Enter',
  '输入或选择TAG': 'Enter or select TAG',
  '输入或选择TAG1': 'Enter or select TAG1',
  '输入或选择TAG2': 'Enter or select TAG2',
  '输入评论... 支持粘贴图片和超链接': 'Write a comment... images and links can be pasted',
  '过滤条件': 'Filters',
  '选中的主线项目将同时新增该需求（可选）': 'The selected mainline projects will also add this requirement (optional)',
  '选择优先级': 'Select priority',
  '选择值': 'Select value',
  '选择关联的底软包': 'Select a related BSP package',
  '选择关联需求 (已选 {count})': 'Select requirements ({count} selected)',
  '选择或输入': 'Select or enter',
  '选择或输入一级分类': 'Select or enter primary category',
  '选择或输入二级分类（可选）': 'Select or enter secondary category (optional)',
  '选择或输入交付物（可选）': 'Select or enter deliverable (optional)',
  '选择或输入自定义交付阶段': 'Select or enter a custom delivery stage',
  '选择或输入（可选）': 'Select or enter (optional)',
  '选择状态': 'Select status',
  '选择硬件类型': 'Select hardware type',
  '选择责任人（默认当前用户）': 'Select assignee (defaults to current user)',
  '选择需要同步的主线项目（可选）': 'Select mainline projects to sync to (optional)',
  '选择验证方法': 'Select verification method',
  '通知列表': 'Notification list',
  '通知列表({count})': 'Notification list ({count})',
  '通知列表中有无法解析的需求，请先移除后再发送': 'The notification list contains requirements that cannot be resolved. Remove them before sending',
  '通知列表为空。可在同一山头任一车型的需求里，点击需求详情卡片的「通知列表」加入（本列表由山头内各车型共用）': 'The notification list is empty. In any vehicle model under the same mountain, click "Notification list" on a requirement card to add it (this list is shared by all vehicle models under the mountain)',
  '重新加载失败，请稍后再试': 'Reload failed, please try again later',
  '重新加载项目阶段': 'Reload project stages',
  '附件': 'Attachment',
  '附件已删除': 'Attachment deleted',
  '需求 #{id}': 'Requirement #{id}',
  '需求 {reqId} 未找到，可能已被删除或不在当前项目中': 'Requirement {reqId} not found; it may have been deleted or does not belong to this project',
  '需求交付首版软件': 'Initial software delivery of requirement',
  '需求变更通知': 'Requirement change notification',
  '需求子类别': 'Requirement subcategory',
  '需求导出Excel': 'Export requirements to Excel',
  '需求导出飞书': 'Export requirements to Feishu',
  '需求已创建': 'Requirement created',
  '需求已删除': 'Requirement deleted',
  '需求已更新': 'Requirement updated',
  '需求描述': 'Requirement description',
  '需求描述至少需要10个字符': 'Requirement description must be at least 10 characters',
  '需求来源': 'Requirement source',
  '需求来源保存失败': 'Failed to save requirement source',
  '需求来源已更新': 'Requirement source updated',
  '需求状态分布 (共 {count} 条)': 'Requirement status distribution ({count} total)',
  '需求管理': 'Requirements',
  '项目查看需求': 'View project requirements',
  '项目特定阶段': 'Project-specific stages',
  '项目自定义阶段': 'Project custom stages',
  '项目配置错误，请联系管理员': 'Project configuration error. Please contact the administrator',
  '项目阶段已重新加载': 'Project stages reloaded',
  '项目：': 'Project:',
  '验证准则': 'Verification criteria',
  '验证方法': 'Verification method',
  // Batch 6: option labels
  'BCR测试': 'BCR test',
  'BCR测试完成': 'BCR test completed',
  'BCR测试已终止': 'BCR test stopped',
  'BCR测试排队中': 'BCR test queued',
  'BCR测试运行中': 'BCR test running',
  '上下电': 'Power on/off',
  '产线离线刷写': 'Offline line flashing',
  '修改需求': 'Requirement updated',
  '初始版本': 'Initial version',
  '删除需求': 'Requirement deleted',
  '加密技术': 'Cryptography',
  '同步修改': 'Synced (updated)',
  '同步删除': 'Synced (deleted)',
  '同步添加': 'Synced (added)',
  '回流排行榜': 'Flowback leaderboard',
  '子类别': 'Subcategory',
  '存储器': 'Storage',
  '存储管理': 'Storage management',
  '安全刷写': 'Secure flashing',
  '安全启动': 'Secure boot',
  '安全存储': 'Secure storage',
  '安全日志': 'Security log',
  '安全访问': 'Secure access',
  '安全诊断': 'Security diagnostics',
  '安全调试': 'Secure debugging',
  '安全通讯': 'Secure communication',
  '密钥管理': 'Key management',
  '操作系统安全': 'OS security',
  '检查': 'Inspection',
  '测试': 'Test',
  '漏洞管理': 'Vulnerability management',
  '热管理': 'Thermal management',
  '电源管理': 'Power management',
  '看门狗': 'Watchdog',
  '自研 AP': 'In-house AP',
  '自研AP': 'In-house AP',
  '调试': 'Debug',
  '软件刷写': 'Software flashing',
  '软件加固': 'Software hardening',
  '防火墙': 'Firewall',
  '集成': 'Integration',
  '需求ID': 'Requirement ID',
  '需求状态': 'Requirement status',
  '需求类别': 'Requirement category',
  '获取需求总览失败': 'Failed to load requirement overview',
  '获取需求列表失败': 'Failed to load requirements',
  '登录失败': 'Login failed',

  // Momenta external release module
  'BSP Package Delivery - 底软包交付管理系统': 'BSP Package Delivery',
  'Git Commit ID (必填)': 'Git Commit ID (required)',
  'Git Tag (可选)': 'Git Tag (optional)',
  'ID': 'ID',
  'IP 地址': 'IP address',
  'Momenta Release Note (文字内容)': 'Momenta Release Note (text content)',
  'Momenta Release Note（可选）': 'Momenta Release Note (optional)',
  'Momenta 测试报告': 'Momenta Test Report',
  'Momenta 测试报告中存在同名文件：{names}，请删除重复文件后再提交': 'Duplicate file names in the Momenta test report: {names}. Remove the duplicates and submit again.',
  'Momenta 测试报告（可选）': 'Momenta Test Report (optional)',
  'Momenta测试报告': 'Momenta Test Report',
  'Momenta测试报告 (文字内容)': 'Momenta Test Report (text content)',
  'Momenta释放产物包不存在': 'Momenta release package not found',
  'Momenta释放产物包管理': 'Momenta Release Package Management',
  'Momenta释放文件': 'Momenta Release Files',
  'Release Note (文字内容)': 'Release Note (text content)',
  '{name} (Git源)': '{name} (Git source)',
  '{name} 的Git信息填写不完整。请要么填写完整（Git URL、分支、Commit ID），要么全部清空。': '{name}: Git information is incomplete. Fill in all fields (Git URL, branch, Commit ID) or clear them all.',
  '三方平台下载链接': 'Third-party download link',
  '三方平台下载链接已复制到剪贴板': 'Third-party download link copied to clipboard',
  '上传第三方平台': 'Upload to third-party platform',
  '下载链接': 'Download link',
  '个已存在于目标文件夹：': ' already exist in the target folder:',
  '产物包尚未上传完成，完成后才能上传第三方平台': 'The package has not finished uploading; third-party upload is available once it completes',
  '产物包未填写车型，无法确定第三方平台存放目录': 'The package has no vehicle model, so the third-party destination folder cannot be determined',
  '产线包已构建，正在上传{name}': 'Build package ready, uploading to {name}',
  '仅建记录': 'Record only',
  '仓库:': 'Repository:',
  '以下文件上传失败，请编辑重试：{names}': 'The following files failed to upload. Edit and retry: {names}',
  '例如：OTA5': 'e.g. OTA5',
  '供应商': 'Supplier',
  '修改前:': 'Before:',
  '修改后:': 'After:',
  '关联底软交付物': 'Linked BSP deliverables',
  '创建了新记录': 'Created a new record',
  '创建人': 'Created by',
  '创建失败': 'Create failed',
  '删除了记录': 'Deleted the record',
  '删除内容:': 'Deleted content:',
  '刷新详情失败': 'Failed to refresh details',
  '加载Momenta释放产物包详情失败': 'Failed to load Momenta release package details',
  '原始文件名:': 'Original file name:',
  '发布请求已带上目录时这里会自动填好，可直接修改。': 'If the publish request included a folder, it is pre-filled here and can be edited.',
  '取消本次上传': 'Cancel this upload',
  '只上传不同名的文件': 'Upload only files with different names',
  '可留空：留空表示上传到分享根目录（如 AY5T 可填 OTA5）。': 'Optional: leave empty to upload to the share root (e.g. enter OTA5 for AY5T).',
  '名称': 'Name',
  '填写第三方制品库目标目录': 'Enter the third-party repository target folder',
  '外部用户无编辑权限': 'External users cannot edit',
  '外部链接': 'External link',
  '已存在的跳过，其余照常上传': 'Existing files are skipped; the rest are uploaded',
  '已提交 {count} 个文件到第三方平台': 'Submitted {count} file(s) to the third-party platform',
  '已提交产线包构建与上传': 'Submitted build package build and upload',
  '已重新提交上传': 'Upload resubmitted',
  '强制上传': 'Force upload',
  '当前项目空间未配置第三方插件，请联系管理员在项目配置中选择': 'No third-party plugin is configured for this project. Ask an administrator to select one in the project settings.',
  '所有文件均已在队列中或已上传成功，未重复提交': 'All files are already queued or uploaded; nothing was resubmitted',
  '所有文件都已存在，没有可上传的文件': 'All files already exist; nothing to upload',
  '手动重试 {count} 次': 'Manually retried {count} time(s)',
  '按创建人搜索': 'Search by creator',
  '按车型搜索': 'Search by vehicle model',
  '提交第三方上传失败': 'Failed to submit the third-party upload',
  '操作描述': 'Description',
  '收起': 'Collapse',
  '文件变更': 'File changes',
  '文件名': 'File name',
  '文件来源': 'File source',
  '文字内容': 'Text content',
  '新增内容:': 'Added content:',
  '新建Momenta释放产物包': 'New Momenta release package',
  '暂无文件变更': 'No file changes',
  '更新了记录': 'Updated the record',
  '未找到项目信息': 'Project information not found',
  '未知': 'Unknown',
  '未知文件': 'Unknown file',
  '本次上传已在进行中或已完成，未重复提交': 'This upload is already in progress or complete; nothing was resubmitted',
  '本次共 {total} 个文件，其中': 'Of {total} files,',
  '正在上传{name}': 'Uploading to {name}',
  '正在上传第三方平台': 'Uploading to third-party platform',
  '正在上传第三方平台，完成后才能编辑': 'Uploading to the third-party platform; editing is available once it completes',
  '正在上传第三方平台，请等待本次上传完成': 'Uploading to the third-party platform; wait for this upload to finish',
  '正在构建产线包': 'Building the build package',
  '测试报告 (文字内容)': 'Test Report (text content)',
  '版本校验未通过': 'Version check failed',
  '版本校验通过': 'Version check passed',
  '版本类型': 'Version type',
  '释放目的': 'Release purpose',
  'CUSTOMER用于给客户释放': 'CUSTOMER — release to customer',
  'TEST用于测试上传流程': 'TEST — test the upload flow',
  '用于给客户释放': 'release to customer',
  '用于测试上传流程': 'test the upload flow',
  '目标文件夹已存在同名文件': 'Files with the same name already exist in the target folder',
  '目标文件夹已存在同名文件。': 'Files with the same name already exist in the target folder.',
  '目标目录': 'Target folder',
  '目标目录长度不能超过 500 个字符': 'The target folder cannot exceed 500 characters',
  '第三方上传与交付看板仍只使用首个车型': 'Third-party upload and the delivery board still use only the first vehicle model',
  '系统': 'System',
  '编辑Momenta释放产物包': 'Edit Momenta release package',
  '自动上传': 'Auto upload',
  '自动重试 {count} 次': 'Automatically retried {count} time(s)',
  '获取详情失败': 'Failed to load details',
  '覆盖': 'Overwrite',
  '该文件未存于 BSP，链接由 RPA 提供': 'This file is not stored in BSP; the link is provided by RPA',
  '请填写第三方制品库的目标目录': 'Enter the target folder in the third-party repository',
  '请输入名称': 'Enter a name',
  '请选择关联的底软交付物（可多选，选填）': 'Select linked BSP deliverables (multiple, optional)',
  '输入 Momenta Release Note 内容': 'Enter Momenta Release Note content',
  '输入 Momenta 测试报告内容': 'Enter Momenta test report content',
  '输入Momenta释放产物包名称': 'Enter the Momenta release package name',
  '输入内容': 'Enter content',
  '输入说明信息': 'Enter a description',
  '选择「覆盖」会替换平台上的原文件；选择「只上传不同名的文件」则保留原文件，只传其余的；选择「取消本次上传」则一个文件都不传。': 'Choosing "Overwrite" replaces the original files on the platform; "Upload only files with different names" keeps the originals and uploads the rest; "Cancel this upload" uploads nothing.',
  '选择子车型（可多选）': 'Select vehicle subtypes (multiple)',
  '选择版本类型（选填）': 'Select a version type (optional)',
  '选择车型（可多选）': 'Select vehicle models (multiple)',
  '重试失败': 'Retry failed',
  '预计 10-30 分钟': 'Est. 10-30 minutes',
  '（大小一致）': '(same size)',
  '（大小不同：平台 {remote} → 本次 {local}）': '(different size: platform {remote} → this upload {local})',
  '（大小未知）': '(size unknown)',
  '（待删除）': '(pending deletion)',
  'SOP/OTA版本': 'SOP/OTA version',
  '工程版本': 'Engineering version',
  '客户评测': 'Customer evaluation',
  '正式版本': 'Official version',
};
